/**
 * The ocean surface.
 *
 * The mesh is a polar grid centred on the camera: ring radii grow
 * geometrically, so triangles stay roughly constant in screen space from a
 * metre away out to the horizon. About 90k vertices covers four kilometres with
 * decimetre detail underfoot, and because the displacement is a function of
 * world position the water stays put while the grid slides along beneath it.
 *
 * Shading is done by hand rather than through a lit material. Reflection
 * samples the same `skyColor` function that paints the sky, which means the sun
 * glitter is automatically in the right place and the horizon joins without a
 * seam.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  Mesh,
  MeshBasicNodeMaterial,
  Vector2,
  Vector3,
  Vector4,
} from 'three/webgpu'
import {
  Fn,
  Loop,
  color,
  cameraPosition,
  float,
  fwidth,
  mix,
  mx_noise_float,
  mx_noise_vec3,
  normalize,
  positionLocal,
  reflect,
  sin,
  texture,
  uniform,
  uniformArray,
  varying,
  vec2,
  vec3,
} from 'three/tsl'
import { HullWaterMasks } from './HullWaterMasks.ts'
import { createOceanDetailTexture } from './oceanDetailTexture.ts'
import type { TSLNode } from './tslTypes.ts'

import { HAZE_COLOR, STORM_HAZE_COLOR, skyColor, stormUniform, sunDirectionUniform } from './Sky.ts'
import { gerstnerSurface, waveTimeUniform } from './waves.ts'

const DEEP = new Color('#102e43')
const SHALLOW = new Color('#265b70')
const SCATTER = new Color('#39887e')
const FOAM = new Color('#e8f2f4')

/** Beyond this the waves flatten out; short of it they are at full height. */
const WAVE_FADE_NEAR = 700
const WAVE_FADE_FAR = 2400

const OCEAN_RADIUS = 4000
const INNER_RADIUS = 0.8
const RINGS = 300
const SEGMENTS = 288

/**
 * Irregular surface detail, inspired by https://jsfiddle.net/gz76849v/.
 * Use Three's MaterialX noise through TSL, so both GPU backends share it.
 * These are shading normals only: the shared Gerstner surface still determines
 * hull buoyancy. Each frequency fades as it becomes smaller than a pixel.
 */
const rippleSlope = /*#__PURE__*/ Fn(([p, footprint]: [TSLNode, TSLNode]) => {
  const clock = waveTimeUniform
  const drift = vec2(clock.mul(0.32), clock.mul(0.12))
  const warp = mx_noise_vec3(vec3(p.mul(0.065), clock.mul(0.045))).xy.mul(2.4)
  const q = p.add(warp)
  const slope = vec2(0).toVar()
  for (const [frequency, strength, speed] of [[0.32, 0.24, 1], [1.1, 0.13, -0.7], [3.7, 0.065, 0.45]]) {
    const resolved = footprint.mul(frequency).smoothstep(0.25, 0.9).oneMinus()
    const detail = mx_noise_vec3(vec3(q.mul(frequency).sub(drift.mul(speed)), clock.mul(0.08)))
    slope.addAssign(detail.xy.mul(strength).mul(resolved))
  }
  return slope.mul(mix(float(0.75), float(1.35), stormUniform))
})

/** Broken wind-stretched patches, shared by whitecaps and vessel froth. */
const foamTexture = /*#__PURE__*/ Fn(([p, footprint]: [TSLNode, TSLNode]) => {
  const drift = vec2(waveTimeUniform.mul(0.22), waveTimeUniform.mul(0.09))
  const q = p.sub(drift)
  const wind = vec2(q.x.mul(0.94).add(q.y.mul(0.34)), q.y.mul(0.94).sub(q.x.mul(0.34)))
  const streaks = mx_noise_float(wind.mul(vec2(0.12, 0.5))).mul(0.5).add(0.5)
  const cells = mx_noise_float(q.mul(1.4)).mul(0.5).add(0.5)
  const resolved = footprint.mul(1.4).smoothstep(0.25, 0.9).oneMinus()
  const holes = mix(float(0.55), cells.smoothstep(0.27, 0.67), resolved)
  return streaks.smoothstep(0.35, 0.7).mul(holes)
})

/** Vessels the water reacts to. Two is enough for a tug and her tow. */
export const WAKE_SOURCES = 2

export interface WakeSource {
  x: number
  z: number
  yaw: number
  speed: number
  /** Extra centre-streak churn, 0..1 — throttle, for prop wash at low speed. */
  wash: number
  /** Half-width of the trailing hull end, metres — sets where the wedge starts. */
  halfBeam: number
}

export class Ocean {
  readonly mesh: Mesh
  readonly hullMasks = new HullWaterMasks()
  private readonly center = uniform(new Vector2())
  /** Per source: (x, z, forwardX, forwardZ). */
  private readonly wakePose = uniformArray(
    Array.from({ length: WAKE_SOURCES }, () => new Vector4(0, 0, 0, 1)),
  )
  /** Per source: (speed, wash, halfBeam, unused). */
  private readonly wakeState = uniformArray(
    Array.from({ length: WAKE_SOURCES }, () => new Vector4(0, 0, 1, 0)),
  )

  constructor(mobile = false) {
    const geometry = buildPolarGrid(mobile ? 160 : RINGS, mobile ? 144 : SEGMENTS, INNER_RADIUS, OCEAN_RADIUS)
    const material = new MeshBasicNodeMaterial({ fog: false })
    const detail = mobile ? texture(createOceanDetailTexture()) : null

    // --- vertex stage -----------------------------------------------------

    // The grid is authored around the origin and the mesh is moved to the
    // camera, so world XZ is local XZ plus the mesh centre.
    const baseWorld = vec3(positionLocal.x.add(this.center.x), 0, positionLocal.z.add(this.center.y))
    const distance = baseWorld.xz.sub(cameraPosition.xz).length()
    const waveFade = distance.smoothstep(WAVE_FADE_NEAR, WAVE_FADE_FAR).oneMinus()

    const vertexNormal = vec3(0, 1, 0).toVar()
    const vertexFoam = float(0).toVar()
    const offset = gerstnerSurface(baseWorld, vertexNormal, vertexFoam).mul(waveFade)

    material.positionNode = positionLocal.add(offset)

    // Carry the *undisplaced* position across to the fragment stage. Evaluating
    // the wave sum again per pixel costs a little but gives crisp crest
    // highlights that vertex-interpolated normals wash out.
    const vBase = varying(baseWorld, 'vBaseWorld')
    const vWorld = varying(baseWorld.add(offset), 'vWorldPos')
    const vFade = varying(waveFade, 'vWaveFade')
    const vDistance = varying(distance, 'vDistance')
    const vNormal = varying(vertexNormal, 'vWaveNormal')
    const vFoam = varying(vertexFoam, 'vWaveFoam')

    // --- fragment stage ---------------------------------------------------

    material.colorNode = Fn(() => {
      const surfaceNormal = vec3(0, 1, 0).toVar()
      const foam = float(0).toVar()
      if (mobile) {
        // The same displaced waves and buoyancy; reuse their vertex results.
        surfaceNormal.assign(normalize(vNormal))
        foam.assign(vFoam)
      } else gerstnerSurface(vBase, surfaceNormal, foam)

      // Flatten both the normal and the foam as the waves fade out with range.
      const normal = mix(vec3(0, 1, 0), surfaceNormal, vFade).toVar()

      // Screen-space filtering also handles high cameras and grazing angles.
      const footprint = fwidth(vBase.xz).length().max(0.001).toVar()
      const detailA = detail?.sample(vBase.xz.mul(.08).add(vec2(waveTimeUniform.mul(.007),waveTimeUniform.mul(.003))))
      const detailB = detail?.sample(vBase.zx.mul(.28).add(vec2(waveTimeUniform.mul(-.005),waveTimeUniform.mul(.004))))
      const slope = (detailA && detailB
        ? detailA.xy.add(detailB.xy.mul(.45)).sub(.725).mul(.85).mul(mix(float(.75),float(1.35),stormUniform))
        : rippleSlope(vBase.xz, footprint)).mul(vFade)
      normal.assign(normalize(normal.add(vec3(slope.x.negate(), 0, slope.y.negate()))))

      const viewDir = normalize(vWorld.sub(cameraPosition))

      // Reflect, then clamp the ray just above horizontal. On the back of a
      // steep wave the raw reflection dips below the horizon and picks up the
      // dull ground tone, which reads as dirt on the water.
      const reflectDir = reflect(viewDir, normal)
      const reflection = skyColor(vec3(reflectDir.x, reflectDir.y.abs().max(0.01), reflectDir.z))

      // Schlick, with water's 2% normal-incidence reflectance.
      const cosTheta = viewDir.negate().dot(normal).clamp(0, 1)
      const fresnel = float(0.02).add(float(0.98).mul(cosTheta.oneMinus().pow(5)))

      // Mostly blue body colour. Height contributes subtly, avoiding the broad
      // luminous green bands that made the old surface look painted on.
      const heightFactor = vWorld.y.mul(0.12).add(0.35).clamp(0, 1)
      const body = mix(color(DEEP), color(SHALLOW), heightFactor).toVar()
      body.assign(mix(body, vec3(0.025, 0.045, 0.055), stormUniform.mul(0.65)))

      const towardSun = viewDir.dot(sunDirectionUniform.negate()).max(0).pow(4)
      const crestLight = vWorld.y.max(0).div(vWorld.y.max(0).add(1.5))
      body.addAssign(color(SCATTER).mul(towardSun.mul(crestLight).mul(0.4)).mul(stormUniform.oneMinus()))

      const surface = mix(body, reflection, fresnel).toVar()

      // Broaden the existing reflected sun into a glitter path. The sky still
      // provides the disc; these softer lobes represent unresolved small waves.
      const halfVector = normalize(sunDirectionUniform.sub(viewDir))
      const sunFacing = normal.dot(halfVector).max(0)
      const glints = sunFacing.pow(180).mul(0.65).add(sunFacing.pow(36).mul(0.1))
      surface.addAssign(vec3(1.0, 0.94, 0.82).mul(glints).mul(stormUniform.oneMinus()))

      // Keep the compression-based crest mask, but break up its coverage.
      // No extra displacement means rendered wave heights and physics agree.
      const froth = (detailA && detailB ? detailA.z.smoothstep(.3,.7).mul(detailB.z.smoothstep(.2,.65)) : foamTexture(vBase.xz, footprint)).toVar()
      const whitecaps = foam.smoothstep(0.18, 0.7).mul(froth)
        .mul(mix(float(0.42), float(0.85), stormUniform)).mul(vFade)
      surface.assign(mix(surface, color(FOAM), whitecaps))

      // --- vessel wakes ---------------------------------------------------
      // Each source leaves a foam wedge at the Kelvin half-angle (~19.5°,
      // tan ≈ 0.354) plus a churned centre streak from the propeller. The
      // pattern is stamped in the vessel's own frame, so it turns when the
      // boat does. World-space froth breaks up the wedge and propeller churn.
      const wake = float(0).toVar()
      Loop(WAKE_SOURCES, ({ i }) => {
        const pose: TSLNode = this.wakePose.element(i)
        const state: TSLNode = this.wakeState.element(i)
        const speed = state.x
        const wash = state.y
        const halfBeam = state.z

        const d = vBase.xz.sub(pose.xy)
        const fwd = pose.zw
        // Distance behind the wake origin in its travel frame, and abeam.
        const behind = d.dot(fwd).negate()
        const lateral = d.x.mul(fwd.y).sub(d.y.mul(fwd.x)).abs()

        const strength = speed.div(2.5).clamp(0, 1).max(wash.mul(0.55))
        // Shorter than before: a long trail read as a rope stretching across
        // the top-down view. The wake should churn behind the boat, not draw a
        // line to the horizon.
        const trailLength = speed.mul(8).add(wash.mul(7)).clamp(8, 70)
        const alongFade = behind.div(trailLength).clamp(0, 1).oneMinus().pow(1.6)
        const inTrail = behind.smoothstep(-1.5, 1.5)

        // Kelvin wedge: edges brighten, interior stays lighter.
        const wedgeWidth = halfBeam.add(behind.mul(0.354))
        const edge = lateral.sub(wedgeWidth).abs().smoothstep(1.6, 0.0).mul(0.7)
        const interior = lateral.smoothstep(wedgeWidth, wedgeWidth.mul(0.25)).mul(0.28)

        // Prop wash: a hot narrow streak that widens slowly and churns with
        // a moving banding pattern so it reads as turbulence, not paint.
        const streakWidth = halfBeam.mul(0.5).add(behind.mul(0.05))
        const streak = lateral.smoothstep(streakWidth, 0.0)
        const churn = sin(behind.mul(1.7).sub(waveTimeUniform.mul(6)))
          .mul(0.5)
          .add(0.5)
          .mul(sin(lateral.mul(2.3).add(behind.mul(0.6))).mul(0.5).add(0.5))
          .mul(0.45)
          .add(0.55)
        // Kept faint: a bright straight centre streak reads as a tow rope from
        // above. Just enough to suggest churn without drawing a line.
        const washTerm = streak.mul(churn).mul(wash.mul(0.22).add(speed.div(8).clamp(0, 0.22)))

        wake.addAssign(edge.add(interior).add(washTerm).mul(alongFade).mul(inTrail).mul(strength))
      })
      const wakeCoverage = wake.mul(froth.mul(0.9).add(0.25)).clamp(0, 0.8)
      surface.assign(mix(surface, color(FOAM), wakeCoverage))

      // Sink the far water into the same haze the sky fades to — which under a
      // storm is the storm's grey, and much nearer.
      const haze = mix(color(HAZE_COLOR), color(STORM_HAZE_COLOR), stormUniform)
      const hazeNear = mix(float(400), float(150), stormUniform)
      const hazeFar = mix(float(3000), float(1400), stormUniform)
      const finalColor = mix(surface, haze, vDistance.smoothstep(hazeNear, hazeFar))
      this.hullMasks.clip(vWorld)
      return finalColor
    })()

    this.mesh = new Mesh(geometry, material)
    this.mesh.frustumCulled = false
    this.mesh.renderOrder = -100
    this.mesh.name = 'ocean'
  }

  /** Slide the grid under the camera. Call once per frame, before rendering. */
  update(cameraPositionWorld: Vector3): void {
    this.mesh.position.set(cameraPositionWorld.x, 0, cameraPositionWorld.z)
    this.center.value.set(cameraPositionWorld.x, cameraPositionWorld.z)
  }

  /** Feed the wake shader this frame's vessel states. Order is stable: slot
   * per vessel, so a stopped boat's trail fades in place rather than jumping. */
  updateWakes(sources: readonly WakeSource[]): void {
    for (let i = 0; i < WAKE_SOURCES; i++) {
      const pose = this.wakePose.array[i] as Vector4
      const state = this.wakeState.array[i] as Vector4
      const source = sources[i]
      if (!source) {
        state.set(0, 0, 1, 0)
        continue
      }
      pose.set(source.x, source.z, Math.sin(source.yaw), Math.cos(source.yaw))
      state.set(source.speed, source.wash, source.halfBeam, 0)
    }
  }
}

/**
 * A disc of triangles with geometrically-spaced rings.
 *
 * Ring `i` sits at `r0 * (rMax / r0) ^ (i / rings)`, so every ring is the same
 * fixed percentage further out than the last. Combined with a constant angular
 * step that keeps triangles near-square at every range — the property that
 * makes a fixed vertex budget stretch from underfoot to the horizon.
 */
function buildPolarGrid(rings: number, segments: number, r0: number, rMax: number): BufferGeometry {
  const vertexCount = 1 + (rings + 1) * segments
  const positions = new Float32Array(vertexCount * 3)
  const normals = new Float32Array(vertexCount * 3)

  // Vertex 0 is the centre cap, closing the hole inside the innermost ring.
  normals[1] = 1

  const growth = Math.log(rMax / r0)
  let p = 3

  for (let i = 0; i <= rings; i++) {
    const radius = r0 * Math.exp(growth * (i / rings))
    for (let j = 0; j < segments; j++) {
      const theta = (j / segments) * Math.PI * 2
      positions[p] = Math.cos(theta) * radius
      positions[p + 1] = 0
      positions[p + 2] = Math.sin(theta) * radius
      normals[p + 1] = 1
      p += 3
    }
  }

  const triangleCount = segments + rings * segments * 2
  const indices = new Uint32Array(triangleCount * 3)
  let t = 0

  const ringStart = (i: number) => 1 + i * segments

  // Centre fan. Winding is chosen so faces point up (+Y).
  for (let j = 0; j < segments; j++) {
    const a = ringStart(0) + j
    const b = ringStart(0) + ((j + 1) % segments)
    indices[t++] = 0
    indices[t++] = b
    indices[t++] = a
  }

  for (let i = 0; i < rings; i++) {
    for (let j = 0; j < segments; j++) {
      const jNext = (j + 1) % segments
      const inner = ringStart(i) + j
      const innerNext = ringStart(i) + jNext
      const outer = ringStart(i + 1) + j
      const outerNext = ringStart(i + 1) + jNext

      indices[t++] = inner
      indices[t++] = outerNext
      indices[t++] = outer

      indices[t++] = inner
      indices[t++] = innerNext
      indices[t++] = outerNext
    }
  }

  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(positions, 3))
  geometry.setAttribute('normal', new BufferAttribute(normals, 3))
  geometry.setIndex(new BufferAttribute(indices, 1))
  geometry.boundingSphere = null
  return geometry
}
